import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import {
  Settings2,
  Clock,
  MapPin,
  Calendar,
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  Play,
  Pause,
  Loader2,
  Shield,
  Zap,
  CheckCircle2,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Rule Types with icons and colors
const RULE_TYPES = {
  time_check: { icon: Clock, color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  service_area: { icon: MapPin, color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  inventory_check: { icon: Shield, color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
  service_type: { icon: Zap, color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' },
  specialist_availability: { icon: CheckCircle2, color: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300' },
  custom: { icon: Settings2, color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300' }
};

const DAY_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

export const AutomationPage = () => {
  const { t } = useTranslation(['rules', 'common']);
  const [activeTab, setActiveTab] = useState('rules');
  
  // Rules state
  const [rules, setRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(true);
  const [ruleFormOpen, setRuleFormOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  
  // Business hours state
  const [businessHours, setBusinessHours] = useState({ slots: [], timezone: 'America/Bogota' });
  const [loadingHours, setLoadingHours] = useState(true);
  const [savingHours, setSavingHours] = useState(false);
  
  // Holidays state
  const [holidays, setHolidays] = useState([]);
  const [loadingHolidays, setLoadingHolidays] = useState(true);
  const [holidayFormOpen, setHolidayFormOpen] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ date: '', name: '', is_working: false });
  
  // Service zones state
  const [serviceZones, setServiceZones] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingZones, setLoadingZones] = useState(true);
  const [zoneFormOpen, setZoneFormOpen] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  
  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', id: '', name: '' });

  useEffect(() => {
    fetchRules();
    fetchBusinessHours();
    fetchHolidays();
    fetchServiceZones();
    fetchCities();
  }, []);

  // ============== API CALLS ==============
  
  const fetchRules = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rules`);
      setRules(response.data.data || []);
    } catch (error) {
      console.error('Error fetching rules:', error);
    } finally {
      setLoadingRules(false);
    }
  };

  const fetchBusinessHours = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rules/config/business-hours`);
      setBusinessHours(response.data);
    } catch (error) {
      console.error('Error fetching business hours:', error);
    } finally {
      setLoadingHours(false);
    }
  };

  const fetchHolidays = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rules/config/holidays`);
      setHolidays(response.data || []);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    } finally {
      setLoadingHolidays(false);
    }
  };

  const fetchServiceZones = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rules/config/service-zones`);
      setServiceZones(response.data || []);
    } catch (error) {
      console.error('Error fetching service zones:', error);
    } finally {
      setLoadingZones(false);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/locations/cities?limit=200`);
      setCities(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  // ============== RULES HANDLERS ==============

  const handleToggleRule = async (ruleId) => {
    try {
      const response = await axios.post(`${API_URL}/api/rules/${ruleId}/toggle`);
      setRules(prev => prev.map(r => 
        r.id === ruleId ? { ...r, is_active: response.data.is_active } : r
      ));
      toast.success(t('messages.ruleToggled'));
    } catch (error) {
      toast.error(t('messages.errorSaving'));
    }
  };

  const handleDeleteRule = async () => {
    try {
      await axios.delete(`${API_URL}/api/rules/${deleteDialog.id}`);
      setRules(prev => prev.filter(r => r.id !== deleteDialog.id));
      toast.success(t('messages.ruleDeleted'));
      setDeleteDialog({ open: false, type: '', id: '', name: '' });
    } catch (error) {
      toast.error(t('messages.errorDeleting'));
    }
  };

  // ============== BUSINESS HOURS HANDLERS ==============

  const handleHourChange = (dayIndex, field, value) => {
    setBusinessHours(prev => ({
      ...prev,
      slots: prev.slots.map(slot =>
        slot.day_of_week === dayIndex ? { ...slot, [field]: value } : slot
      )
    }));
  };

  const handleSaveBusinessHours = async () => {
    setSavingHours(true);
    try {
      await axios.put(`${API_URL}/api/rules/config/business-hours`, businessHours);
      toast.success(t('messages.hoursUpdated'));
    } catch (error) {
      toast.error(t('messages.errorSaving'));
    } finally {
      setSavingHours(false);
    }
  };

  // ============== HOLIDAYS HANDLERS ==============

  const handleAddHoliday = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/rules/config/holidays`, newHoliday);
      setHolidays(prev => [...prev, response.data]);
      setHolidayFormOpen(false);
      setNewHoliday({ date: '', name: '', is_working: false });
      toast.success(t('messages.holidayCreated'));
    } catch (error) {
      toast.error(t('messages.errorSaving'));
    }
  };

  const handleDeleteHoliday = async () => {
    try {
      await axios.delete(`${API_URL}/api/rules/config/holidays/${deleteDialog.id}`);
      setHolidays(prev => prev.filter(h => h.id !== deleteDialog.id));
      toast.success(t('messages.holidayDeleted'));
      setDeleteDialog({ open: false, type: '', id: '', name: '' });
    } catch (error) {
      toast.error(t('messages.errorDeleting'));
    }
  };

  // ============== SERVICE ZONES HANDLERS ==============

  const handleDeleteZone = async () => {
    try {
      await axios.delete(`${API_URL}/api/rules/config/service-zones/${deleteDialog.id}`);
      setServiceZones(prev => prev.filter(z => z.id !== deleteDialog.id));
      toast.success(t('messages.zoneDeleted'));
      setDeleteDialog({ open: false, type: '', id: '', name: '' });
    } catch (error) {
      toast.error(t('messages.errorDeleting'));
    }
  };

  // ============== RENDER ==============

  return (
    <div className="space-y-6" data-testid="automation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('title')}</h1>
          <p className="text-muted-foreground">{t('subtitle')}</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="rules" className="gap-2" data-testid="tab-rules">
            <Settings2 className="h-4 w-4" />
            <span className="hidden sm:inline">{t('rules.title')}</span>
          </TabsTrigger>
          <TabsTrigger value="hours" className="gap-2" data-testid="tab-hours">
            <Clock className="h-4 w-4" />
            <span className="hidden sm:inline">{t('businessHours.title')}</span>
          </TabsTrigger>
          <TabsTrigger value="holidays" className="gap-2" data-testid="tab-holidays">
            <Calendar className="h-4 w-4" />
            <span className="hidden sm:inline">{t('holidays.title')}</span>
          </TabsTrigger>
          <TabsTrigger value="zones" className="gap-2" data-testid="tab-zones">
            <MapPin className="h-4 w-4" />
            <span className="hidden sm:inline">{t('serviceZones.title')}</span>
          </TabsTrigger>
        </TabsList>

        {/* Rules Tab */}
        <TabsContent value="rules" className="space-y-4" data-testid="rules-tab-content">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('rules.title')}</CardTitle>
                <CardDescription>{t('rules.description')}</CardDescription>
              </div>
              <Button onClick={() => { setSelectedRule(null); setRuleFormOpen(true); }} data-testid="add-rule-btn">
                <Plus className="h-4 w-4 mr-2" />
                {t('rules.add')}
              </Button>
            </CardHeader>
            <CardContent>
              {loadingRules ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : rules.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Settings2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t('rules.empty')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {rules.map(rule => {
                    const TypeIcon = RULE_TYPES[rule.type]?.icon || Settings2;
                    const typeColor = RULE_TYPES[rule.type]?.color || 'bg-gray-100 text-gray-800';
                    
                    return (
                      <div
                        key={rule.id}
                        className="flex items-center justify-between p-4 rounded-lg border bg-card hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${typeColor}`}>
                            <TypeIcon className="h-5 w-5" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{rule.name}</span>
                              <Badge variant="outline" className="text-xs">
                                P{rule.priority}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {rule.description || t(`types.${rule.type}`)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={rule.is_active}
                            onCheckedChange={() => handleToggleRule(rule.id)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelectedRule(rule); setRuleFormOpen(true); }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => setDeleteDialog({
                              open: true,
                              type: 'rule',
                              id: rule.id,
                              name: rule.name
                            })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Business Hours Tab */}
        <TabsContent value="hours" className="space-y-4" data-testid="hours-tab-content">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('businessHours.title')}</CardTitle>
                <CardDescription>{t('businessHours.description')}</CardDescription>
              </div>
              <Button onClick={handleSaveBusinessHours} disabled={savingHours} data-testid="save-hours-btn">
                {savingHours && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t('common:save')}
              </Button>
            </CardHeader>
            <CardContent>
              {loadingHours ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-4">
                  {businessHours.slots?.map((slot, index) => (
                    <div
                      key={slot.day_of_week}
                      className="flex items-center gap-4 p-3 rounded-lg border"
                    >
                      <div className="w-32">
                        <span className="font-medium">{DAY_NAMES[slot.day_of_week]}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={slot.is_working_day}
                          onCheckedChange={(checked) => handleHourChange(slot.day_of_week, 'is_working_day', checked)}
                        />
                        <span className="text-sm text-muted-foreground">
                          {slot.is_working_day ? 'Laboral' : 'No laboral'}
                        </span>
                      </div>
                      {slot.is_working_day && (
                        <>
                          <div className="flex items-center gap-2">
                            <Label className="text-sm">Apertura:</Label>
                            <Input
                              type="time"
                              value={slot.open_time}
                              onChange={(e) => handleHourChange(slot.day_of_week, 'open_time', e.target.value)}
                              className="w-32"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-sm">Cierre:</Label>
                            <Input
                              type="time"
                              value={slot.close_time}
                              onChange={(e) => handleHourChange(slot.day_of_week, 'close_time', e.target.value)}
                              className="w-32"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Holidays Tab */}
        <TabsContent value="holidays" className="space-y-4" data-testid="holidays-tab-content">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('holidays.title')}</CardTitle>
                <CardDescription>{t('holidays.description')}</CardDescription>
              </div>
              <Button onClick={() => setHolidayFormOpen(true)} data-testid="add-holiday-btn">
                <Plus className="h-4 w-4 mr-2" />
                {t('holidays.add')}
              </Button>
            </CardHeader>
            <CardContent>
              {loadingHolidays ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : holidays.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t('holidays.empty')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {holidays.sort((a, b) => a.date.localeCompare(b.date)).map(holiday => (
                    <div
                      key={holiday.id}
                      className="flex items-center justify-between p-3 rounded-lg border"
                    >
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <div className="text-lg font-bold">{new Date(holiday.date + 'T12:00:00').getDate()}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(holiday.date + 'T12:00:00').toLocaleDateString('es', { month: 'short' })}
                          </div>
                        </div>
                        <div>
                          <span className="font-medium">{holiday.name}</span>
                          <Badge variant={holiday.is_working ? 'secondary' : 'destructive'} className="ml-2">
                            {holiday.is_working ? 'Laboral' : 'No laboral'}
                          </Badge>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => setDeleteDialog({
                          open: true,
                          type: 'holiday',
                          id: holiday.id,
                          name: holiday.name
                        })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Service Zones Tab */}
        <TabsContent value="zones" className="space-y-4" data-testid="zones-tab-content">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('serviceZones.title')}</CardTitle>
                <CardDescription>{t('serviceZones.description')}</CardDescription>
              </div>
              <Button onClick={() => { setSelectedZone(null); setZoneFormOpen(true); }} data-testid="add-zone-btn">
                <Plus className="h-4 w-4 mr-2" />
                {t('serviceZones.add')}
              </Button>
            </CardHeader>
            <CardContent>
              {loadingZones ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : serviceZones.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t('serviceZones.empty')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {serviceZones.map(zone => (
                    <div
                      key={zone.id}
                      className="flex items-center justify-between p-4 rounded-lg border"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 rounded-lg bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                          <MapPin className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="font-medium">{zone.zone_name}</div>
                          <p className="text-sm text-muted-foreground">
                            {zone.city_name} • {zone.neighborhoods?.length || 0} barrios
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={zone.is_active ? 'default' : 'secondary'}>
                          {zone.is_active ? 'Activa' : 'Inactiva'}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedZone(zone); setZoneFormOpen(true); }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => setDeleteDialog({
                            open: true,
                            type: 'zone',
                            id: zone.id,
                            name: zone.zone_name
                          })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Rule Form Sheet */}
      <RuleFormSheet
        open={ruleFormOpen}
        onOpenChange={setRuleFormOpen}
        rule={selectedRule}
        onSave={(savedRule) => {
          if (selectedRule) {
            setRules(prev => prev.map(r => r.id === savedRule.id ? savedRule : r));
          } else {
            setRules(prev => [...prev, savedRule]);
          }
          setRuleFormOpen(false);
        }}
      />

      {/* Holiday Form Sheet */}
      <Sheet open={holidayFormOpen} onOpenChange={setHolidayFormOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('holidays.add')}</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            <div className="space-y-2">
              <Label>{t('holidays.date')}</Label>
              <Input
                type="date"
                value={newHoliday.date}
                onChange={(e) => setNewHoliday(prev => ({ ...prev, date: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('holidays.name')}</Label>
              <Input
                value={newHoliday.name}
                onChange={(e) => setNewHoliday(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Día de la Independencia"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={newHoliday.is_working}
                onCheckedChange={(checked) => setNewHoliday(prev => ({ ...prev, is_working: checked }))}
              />
              <Label>{t('holidays.isWorking')}</Label>
            </div>
            <Button
              className="w-full mt-4"
              onClick={handleAddHoliday}
              disabled={!newHoliday.date || !newHoliday.name}
            >
              {t('common:save')}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Zone Form Sheet */}
      <ZoneFormSheet
        open={zoneFormOpen}
        onOpenChange={setZoneFormOpen}
        zone={selectedZone}
        cities={cities}
        onSave={(savedZone) => {
          if (selectedZone) {
            setServiceZones(prev => prev.map(z => z.id === savedZone.id ? savedZone : z));
          } else {
            setServiceZones(prev => [...prev, savedZone]);
          }
          setZoneFormOpen(false);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog(prev => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common:confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('common:deleteConfirmMessage', { name: deleteDialog.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => {
                if (deleteDialog.type === 'rule') handleDeleteRule();
                else if (deleteDialog.type === 'holiday') handleDeleteHoliday();
                else if (deleteDialog.type === 'zone') handleDeleteZone();
              }}
            >
              {t('common:delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ============== RULE FORM SHEET ==============

const RuleFormSheet = ({ open, onOpenChange, rule, onSave }) => {
  const { t } = useTranslation(['rules', 'common']);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'custom',
    priority: 0,
    is_active: true,
    conditions: [],
    match_mode: 'all',
    actions_on_match: [],
    actions_on_no_match: []
  });
  const [saving, setSaving] = useState(false);
  const [ruleTypes, setRuleTypes] = useState([]);
  const [operators, setOperators] = useState([]);

  useEffect(() => {
    if (rule) {
      setFormData({
        name: rule.name || '',
        description: rule.description || '',
        type: rule.type || 'custom',
        priority: rule.priority || 0,
        is_active: rule.is_active !== false,
        conditions: rule.conditions || [],
        match_mode: rule.match_mode || 'all',
        actions_on_match: rule.actions_on_match || [],
        actions_on_no_match: rule.actions_on_no_match || []
      });
    } else {
      setFormData({
        name: '',
        description: '',
        type: 'custom',
        priority: 0,
        is_active: true,
        conditions: [],
        match_mode: 'all',
        actions_on_match: [],
        actions_on_no_match: []
      });
    }
  }, [rule, open]);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [typesRes, opsRes] = await Promise.all([
          axios.get(`${API_URL}/api/rules/types`),
          axios.get(`${API_URL}/api/rules/operators`)
        ]);
        setRuleTypes(typesRes.data || []);
        setOperators(opsRes.data || []);
      } catch (error) {
        console.error('Error fetching meta:', error);
      }
    };
    if (open) fetchMeta();
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      let response;
      if (rule) {
        response = await axios.put(`${API_URL}/api/rules/${rule.id}`, formData);
      } else {
        response = await axios.post(`${API_URL}/api/rules`, formData);
      }
      onSave(response.data);
      toast.success(rule ? t('messages.ruleUpdated') : t('messages.ruleCreated'));
    } catch (error) {
      toast.error(t('messages.errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const addCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, { field: '', operator: 'equals', value: '' }]
    }));
  };

  const updateCondition = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((c, i) => i === index ? { ...c, [field]: value } : c)
    }));
  };

  const removeCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{rule ? t('rules.edit') : t('rules.add')}</SheetTitle>
          <SheetDescription>{t('rules.description')}</SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-180px)] mt-6 pr-4">
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('rules.name')} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ej: Verificar horario de atención"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('rules.description')}</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descripción opcional de la regla"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('rules.type')}</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ruleTypes.map(rt => (
                        <SelectItem key={rt.code} value={rt.code}>{rt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>{t('rules.priority')}</Label>
                  <Input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
                <Label>{formData.is_active ? t('rules.active') : t('rules.inactive')}</Label>
              </div>
            </div>

            {/* Conditions */}
            <Collapsible defaultOpen>
              <CollapsibleTrigger className="flex items-center gap-2 w-full">
                <ChevronRight className="h-4 w-4 transition-transform ui-open:rotate-90" />
                <span className="font-medium">{t('rules.conditions')}</span>
                <Badge variant="secondary" className="ml-auto">{formData.conditions.length}</Badge>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-4 space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <Label className="text-sm">{t('rules.matchMode')}:</Label>
                  <Select
                    value={formData.match_mode}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, match_mode: value }))}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('rules.matchAll')}</SelectItem>
                      <SelectItem value="any">{t('rules.matchAny')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.conditions.map((condition, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 rounded-lg border bg-muted/50">
                    <Input
                      value={condition.field}
                      onChange={(e) => updateCondition(index, 'field', e.target.value)}
                      placeholder="Campo (ej: city)"
                      className="w-32"
                    />
                    <Select
                      value={condition.operator}
                      onValueChange={(value) => updateCondition(index, 'operator', value)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {operators.map(op => (
                          <SelectItem key={op.code} value={op.code}>{op.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={condition.value}
                      onChange={(e) => updateCondition(index, 'value', e.target.value)}
                      placeholder="Valor"
                      className="flex-1"
                    />
                    <Button variant="ghost" size="sm" onClick={() => removeCondition(index)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                
                <Button variant="outline" size="sm" onClick={addCondition}>
                  <Plus className="h-4 w-4 mr-2" />
                  Agregar Condición
                </Button>
              </CollapsibleContent>
            </Collapsible>

            {/* Save Button */}
            <Button
              className="w-full"
              onClick={handleSave}
              disabled={saving || !formData.name}
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('common:save')}
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

// ============== ZONE FORM SHEET ==============

const ZoneFormSheet = ({ open, onOpenChange, zone, cities, onSave }) => {
  const { t } = useTranslation(['rules', 'common']);
  const [formData, setFormData] = useState({
    city_id: '',
    zone_name: '',
    neighborhoods: [],
    radius_km: null,
    is_active: true
  });
  const [neighborhoodInput, setNeighborhoodInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (zone) {
      setFormData({
        city_id: zone.city_id || '',
        zone_name: zone.zone_name || '',
        neighborhoods: zone.neighborhoods || [],
        radius_km: zone.radius_km,
        is_active: zone.is_active !== false
      });
    } else {
      setFormData({
        city_id: '',
        zone_name: '',
        neighborhoods: [],
        radius_km: null,
        is_active: true
      });
    }
  }, [zone, open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      let response;
      if (zone) {
        response = await axios.put(`${API_URL}/api/rules/config/service-zones/${zone.id}`, formData);
      } else {
        response = await axios.post(`${API_URL}/api/rules/config/service-zones`, formData);
      }
      onSave(response.data);
      toast.success(zone ? t('messages.zoneUpdated') : t('messages.zoneCreated'));
    } catch (error) {
      toast.error(t('messages.errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const addNeighborhood = () => {
    if (neighborhoodInput.trim()) {
      setFormData(prev => ({
        ...prev,
        neighborhoods: [...prev.neighborhoods, neighborhoodInput.trim()]
      }));
      setNeighborhoodInput('');
    }
  };

  const removeNeighborhood = (index) => {
    setFormData(prev => ({
      ...prev,
      neighborhoods: prev.neighborhoods.filter((_, i) => i !== index)
    }));
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{zone ? 'Editar Zona' : t('serviceZones.add')}</SheetTitle>
        </SheetHeader>
        <div className="space-y-4 mt-6">
          <div className="space-y-2">
            <Label>{t('serviceZones.city')} *</Label>
            <Select
              value={formData.city_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, city_id: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar ciudad" />
              </SelectTrigger>
              <SelectContent>
                {cities.map(city => (
                  <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>{t('serviceZones.zoneName')} *</Label>
            <Input
              value={formData.zone_name}
              onChange={(e) => setFormData(prev => ({ ...prev, zone_name: e.target.value }))}
              placeholder="Ej: Zona Norte"
            />
          </div>
          
          <div className="space-y-2">
            <Label>{t('serviceZones.neighborhoods')}</Label>
            <div className="flex gap-2">
              <Input
                value={neighborhoodInput}
                onChange={(e) => setNeighborhoodInput(e.target.value)}
                placeholder="Agregar barrio"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addNeighborhood())}
              />
              <Button type="button" variant="outline" onClick={addNeighborhood}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.neighborhoods.map((n, i) => (
                <Badge key={i} variant="secondary" className="gap-1">
                  {n}
                  <button onClick={() => removeNeighborhood(i)} className="ml-1 hover:text-destructive">
                    ×
                  </button>
                </Badge>
              ))}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>{t('serviceZones.radiusKm')}</Label>
            <Input
              type="number"
              value={formData.radius_km || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, radius_km: parseFloat(e.target.value) || null }))}
              placeholder="Opcional"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
            />
            <Label>{formData.is_active ? 'Activa' : 'Inactiva'}</Label>
          </div>
          
          <Button
            className="w-full mt-4"
            onClick={handleSave}
            disabled={saving || !formData.city_id || !formData.zone_name}
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('common:save')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AutomationPage;
